import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { RadioBlockGroup, RadioOptionWrapper, RadioLabel } from 'design-system/src/components/Radio/Radio';
import { ActionCard } from '../../ActionCard/ActionCard';
import { OtherRequestButton } from './shared/OtherRequestButton';
import { Input } from '../../Input';
import { FormLabel } from '../../FormLabel';
import { DropdownWithPlaceholder, DropdownItem } from '../../Dropdown';
import { client, type BenefitPreferenceType } from '../../../api';
import { tasksQueryKey } from '../../../queries';
import { useSitesOfCare } from '../../../hooks/useBenefitPreferences';
import { formatAddress } from '../../../utils';
import AddSiteOfCareModal from '../../settings/AddSiteOfCareModal';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import { TaskActionsLoadingSkeleton } from './TaskActionsLoadingSkeleton';
import type { Task } from '../types';

type BenefitsRoutingConfirmationActionsProps = {
  /** The task ID */
  taskId: string;
  /** The full task object */
  task: Task;
  /** Callback to show the follow-up task modal */
  setShowNewFollowupTaskModal: (show: boolean) => void;
};

const FULFILLMENT_OPTIONS = [
  {
    value: 'buy_and_bill' as const,
    label: 'Buy-and-bill',
    description: 'Your practice purchases and bills the payer directly',
  },
  {
    value: 'white_bagging' as const,
    label: 'Specialty pharmacy (white bagging)',
    description: 'The specialty pharmacy will ship the medication to the site of care',
  },
];

/** Map a fulfillment selection to the backend response type */
const getResponseType = (selection: string): 'process_as_medical_benefits' | 'process_as_pharmacy_benefits' => {
  if (selection === 'buy_and_bill') return 'process_as_medical_benefits';
  return 'process_as_pharmacy_benefits';
};

const SETTINGS_LINK = '/settings?tab=benefitPreferences';

export const BenefitsRoutingConfirmationActions = ({
  taskId,
  task,
  setShowNewFollowupTaskModal,
}: BenefitsRoutingConfirmationActionsProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: sitesData, isLoading: isSitesLoading } = useSitesOfCare();
  const sites = sitesData?.sites ?? [];

  const isCompleted = task.status === 'completed';
  const isDisabled = isCompleted;

  // Derive behavior from two fields:
  // - benefits_preference: prompt_everytime → fulfillment editable; otherwise prefilled
  // - site_of_care_id: set → site disabled; null → site editable
  const isPromptEverytime = task.benefits_preference === 'prompt_everytime';
  const hasSiteOfCare = !!task.site_of_care_id;

  const getInitialFormData = (): { site_of_care_id: string; benefits_preference: BenefitPreferenceType | '' } => {
    // For completed tasks, the backend returns the submitted values
    if (isCompleted && task.site_of_care_id && task.benefits_preference !== 'prompt_everytime') {
      return {
        site_of_care_id: task.site_of_care_id,
        benefits_preference: task.benefits_preference ?? '',
      };
    }
    return {
      site_of_care_id: hasSiteOfCare ? (task.site_of_care_id ?? '') : '',
      benefits_preference: !isPromptEverytime ? (task.benefits_preference ?? '') : '',
    };
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddSiteOfCareModal, setShowAddSiteOfCareModal] = useState(false);

  const siteDropdownOptions = sites.map((site) => ({
    id: site.id,
    label: formatAddress(
      [site.address_line_1, site.address_line_2].filter(Boolean).join(', '),
      site.city,
      site.state,
      site.zipcode,
    ),
  }));

  const handleSubmit = async () => {
    if (!formData.site_of_care_id || !formData.benefits_preference) return;

    const responseType = getResponseType(formData.benefits_preference);

    setIsSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('task_response', responseType);

      const preferenceData = {
        drug_name: task.drug_name ?? null,
        drug_form: task.drug_form ?? null,
        drug_ndc: task.drug_ndc ?? null,
        hcpcs: task.hcpcs ?? null,
        cpt: task.cpt ?? null,
        site_of_care_id: formData.site_of_care_id || null,
        benefits_preference: formData.benefits_preference || null,
        provider_npi: task.provider_npi ?? null,
        rendering_provider_npi: task.rendering_provider_npi ?? null,
      };

      submitData.append(
        'additional_input',
        JSON.stringify({
          preference_scope: 'prescription',
          preference_data: preferenceData,
        }),
      );

      await client.post(`tasks/${taskId}/respond`, submitData);
      toast.custom(() => <Toast variant="success">Task response received.</Toast>);
      navigate('/tasks');
      queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
    } catch (error) {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = !!(formData.site_of_care_id && formData.benefits_preference);

  if (isSitesLoading) {
    return <TaskActionsLoadingSkeleton optionCount={3} />;
  }

  return (
    <>
      {isCompleted && task.completed_at && (
        <div className="mb-4 rounded-medium bg-surface-200 p-3">
          <Typography>
            This task was completed on <strong>{formatStandardDate(task.completed_at)}</strong>
            {task.completed_by_name && (
              <>
                {' '}
                by <strong>{task.completed_by_name}</strong>
              </>
            )}
            .{' '}
            <Link to={SETTINGS_LINK} className="underline">
              Manage fulfillment preferences
            </Link>
          </Typography>
        </div>
      )}

      {!isCompleted && (
        <div className="mb-4 flex flex-col gap-1">
          <Typography weight="bold">Confirm the fulfillment preference for this prescription.</Typography>
          <Typography>
            {isPromptEverytime
              ? 'Tandem will ask for a fulfillment option every time you prescribe this drug, according to your settings.'
              : 'Please select the site of care for this prescription.'}
          </Typography>
        </div>
      )}

      <ActionCard>
        <div className="flex w-full flex-col gap-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <Input label="Drug" value={task.drug_name ?? ''} disabled />
            </div>

            <div className="flex gap-4">
              <div className="flex w-1/2 flex-col">
                <Input label="HCPCS code" value={task.hcpcs ?? ''} disabled />
              </div>
              <div className="flex w-1/2 flex-col">
                <Input label="Admin CPT code" value={task.cpt ?? ''} disabled />
              </div>
            </div>

            <div className="flex flex-col">
              <Input label="Rendering NPI" value={task.rendering_provider_npi ?? ''} disabled />
            </div>

            <div className="flex flex-col">
              <div className="mb-1 flex items-center justify-between">
                <FormLabel htmlFor="site-of-care" className="mb-0">
                  Site of care
                </FormLabel>
                {!isDisabled && !hasSiteOfCare && (
                  <button
                    type="button"
                    onClick={() => setShowAddSiteOfCareModal(true)}
                    className="flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 hover:opacity-80"
                  >
                    <Typography styledAs="bodyExtraSmallSpaceGrotesk" color="var(--purple-700)">
                      + Add site of care
                    </Typography>
                  </button>
                )}
              </div>
              <DropdownWithPlaceholder
                id="site-of-care"
                placeholder="Select site of care"
                value={formData.site_of_care_id}
                onChange={(e) => setFormData({ ...formData, site_of_care_id: e.target.value as string })}
                disabled={isDisabled || hasSiteOfCare}
              >
                {siteDropdownOptions.map((option) => (
                  <DropdownItem key={option.id} value={option.id}>
                    {option.label}
                  </DropdownItem>
                ))}
              </DropdownWithPlaceholder>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <FormLabel>Primary fulfillment preference</FormLabel>
            <RadioBlockGroup $variant="outlined">
              {FULFILLMENT_OPTIONS.map((option) => (
                <RadioOptionWrapper
                  id={`confirmation-${option.value}`}
                  name="fulfillment-confirmation"
                  value={option.value}
                  checked={formData.benefits_preference === option.value}
                  onChange={() => setFormData({ ...formData, benefits_preference: option.value })}
                  key={option.value}
                  variant="outlined"
                  disabled={isDisabled || !isPromptEverytime}
                >
                  <div className="flex flex-col gap-0.5">
                    <RadioLabel $disabled={isDisabled || !isPromptEverytime}>{option.label}</RadioLabel>
                    <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
                      {option.description}
                    </Typography>
                  </div>
                </RadioOptionWrapper>
              ))}
            </RadioBlockGroup>
          </div>
        </div>

        {!isCompleted && (
          <ButtonRow align={Alignment.SpaceBetween} fullWidth>
            <OtherRequestButton setShowNewFollowupTaskModal={setShowNewFollowupTaskModal} />
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              disabled={!isFormValid || isSubmitting}
            >
              Submit
            </Button>
          </ButtonRow>
        )}
      </ActionCard>

      <AddSiteOfCareModal
        open={showAddSiteOfCareModal}
        onClose={() => setShowAddSiteOfCareModal(false)}
        onSiteCreated={(siteId) => setFormData((prev) => ({ ...prev, site_of_care_id: siteId }))}
      />
    </>
  );
};
